import { NgModule, APP_INITIALIZER, Pipe, PipeTransform, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { BrowserModule, DomSanitizer  } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule, Routes } from '@angular/router';
import { LocationStrategy, HashLocationStrategy, PathLocationStrategy } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

// @sinequa/core library
import { WebServicesModule, StartConfigWebService, StartConfig } from "@sinequa/core/web-services";
import { LoginModule, LoginInterceptor, TeamsInitializer, AuthenticationService } from "@sinequa/core/login";
import { IntlModule } from "@sinequa/core/intl";
import { ModalModule } from "@sinequa/core/modal";
import { NotificationsInterceptor } from "@sinequa/core/notification";
import { AuditInterceptor } from "@sinequa/core/app-utils";

// @sinequa/components library
import { BsSearchModule, SearchOptions } from "@sinequa/components/search";
import { BsAutocompleteModule } from "@sinequa/components/autocomplete";
import { BsNotificationModule } from "@sinequa/components/notification";
import { BsFacetModule } from "@sinequa/components/facet";
import { BsActionModule } from "@sinequa/components/action";
import { BsModalModule } from "@sinequa/components/modal";
import { BsBasketsModule } from '@sinequa/components/baskets';
import { BsAlertsModule } from '@sinequa/components/alerts';
import { BsSavedQueriesModule } from '@sinequa/components/saved-queries';
import { UtilsModule, SCREEN_SIZE_RULES } from '@sinequa/components/utils';
import { BsLabelsModule } from '@sinequa/components/labels';
import { APP_HELP_FOLDER_OPTIONS, BsUserSettingsModule } from '@sinequa/components/user-settings';
import { ResultModule } from '@sinequa/components/result';
import { BsFeedbackModule } from '@sinequa/components/feedback';
import { BsPreviewModule } from '@sinequa/components/preview';
import { MetadataModule } from '@sinequa/components/metadata';
import { BsSelectionModule } from '@sinequa/components/selection';
import { BsAdvancedModule } from '@sinequa/components/advanced';
import { BsTimelineModule } from '@sinequa/analytics/timeline';
import { MLModule } from '@sinequa/components/machine-learning';

// Components
import { AppComponent } from "./app.component";
import { HomeComponent } from './home/home.component';
import { SearchComponent } from './search/search.component';
import { PreviewComponent } from './preview/preview.component';
import { SearchFormComponent } from './search-form/search-form.component';
import { AutocompleteExtended } from './search-form/autocomplete-extended.directive';

// Environment
import { environment } from "../environments/environment";

// Help folder options
import { HELP_DEFAULT_FOLDER_OPTIONS } from "../config";


// Initialization of @sinequa/core
export const startConfig: StartConfig = {
    app: "insightvault",
    url: environment.api_url,
    production: environment.production,
    // autoSAMLProvider: environment.autoSAMLProvider,
    auditEnabled: true
};

// @sinequa/core config initializer
export function StartConfigInitializer(startConfigWebService: StartConfigWebService) {
    return () => startConfigWebService.fetchPreLoginAppConfig();
}


// Application routes (see https://angular.io/guide/router)
export const routes: Routes = [
    { path: "home", component: HomeComponent },
    { path: "search", component: SearchComponent },
    { path: "preview", component: PreviewComponent },
    { path: "**", redirectTo: "home" }
];


// Search options (search service)
export const searchOptions: SearchOptions = {
    routes: ["search"],
    homeRoute: "home"
};


// Application languages (intl service)
import { LocalesConfig, Locale } from "@sinequa/core/intl";
import enLocale from "../locales/en";
import frLocale from "../locales/fr";
import deLocale from "../locales/de";
import { HeaderComponent } from './header/header/header.component';
import { FooterComponent } from './footer/footer/footer.component';
import { FiltersComponent } from "./filters/filters.component";
import { FilterModalComponent } from './filter-modal/filter-modal.component';
import { SecurePipe } from "./secure.pipe";
import { ResultThumbnailComponent } from "./results/result-thumbnail/result-thumbnail.component";
import { ResultTitleComponent } from "./results/result-title/result-title.component";
import { AuthorizationInterceptor } from "authorization.interceptor";
import { Type, Provider } from "@angular/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { StickyPopoverDirective } from "./sticky.popover.directive";
import { HtmlNodeComponent } from "./html-node.component";
// import { StickyPopoverDirective } from "./sticky.popover.directive";
// import { AuthorizationInterceptor } from "authorization.interceptor";

declare module "@angular/core" {
    interface ModuleWithProviders<T = any> {
      ngModule: Type<T>;
      providers?: Provider[];
    }
  }
export class AppLocalesConfig implements LocalesConfig {
    defaultLocale: Locale;
    locales?: Locale[];
    constructor() {
        this.locales = [
            { name: "en", display: "msg#locale.en", data: enLocale },
            { name: "fr", display: "msg#locale.fr", data: frLocale },
            { name: "de", display: "msg#locale.de", data: deLocale },
        ];
        this.defaultLocale = this.locales[0];
    }
} 

// Screen size breakpoints (consistent with Bootstrap custom breakpoints in app.scss)
export const breakpoints = {
    xl: "(min-width: 1650px)",
    lg: "(min-width: 1400px) and (max-width: 1649.98px)",
    md: "(min-width: 992px) and (max-width: 1399.98px)",
    sm: "(min-width: 576px) and (max-width: 991.98px)",
    xs: "(max-width: 575.98px)",
};

@Pipe({
    name: 'boldStar'
  })
  export class boldStarPipe implements PipeTransform {
  constructor(private sanitizer : DomSanitizer){
      
  }
  
  restructureToGPT4oStyle(rawInput) {
  const decodeWrappedSummary = (input) => {
    let text = input.trim();
    return text
      .replace(/\\"/g, '"')
      .replace(/\\n/g, "\n")
      .replace(/\\u2011/g, "‑")
      .replace(/\\u2013/g, "–")
      .replace(/\\u2014/g, "—")
      .replace(/\\u2018/g, "‘")
      .replace(/\\u2019/g, "’")
      .replace(/\\u201c/g, "“")
      .replace(/\\u201d/g, "”")
      .replace(/\\u2265/g, "≥")
      .replace(/[ \t]+$/gm, "")
      .replace(/\n{3,}/g, "\n\n");
  }

  const isHeading = (line)=> {
    return /^##\s+/.test(line) || /^###\s+/.test(line);
  }

  const isRule =(line) => {
    return /^\s*---\s*$/.test(line);
  }

  const bulletDepth =(line) => {
    const m = line.match(/^(\s*)-\s+/);
    if (!m) return -1;
    const spaces = m[1].length;
    return spaces > 0 ? 1 : 0;
  }

  const bulletText:any  = (line:any) => {
    return line.replace(/^\s*-\s+/, "").trim();
  }

  const isColonLabel = (text) => {
    return /:\s*$/.test(text);
  }

  const stripTrailingColon = (text) => {
    return text.replace(/:\s*$/, "").trim();
  }

  const joinAsSentenceParts = (parts) => {
    const cleaned = parts
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s.replace(/\s+/g, " "));

    if (!cleaned.length) return "";

    return cleaned
      .map((part, idx) => {
        if (idx === 0) return part;
        return part;
      })
      .join(" ");
  }

  const ensureParagraphPunctuation = (text) => {
    const t = text.trim();
    if (!t) return "";
    if (/[.!?]$/.test(t)) return t;
    return t + ".";
  }

  const parseBulletBlock = (lines, startIndex) => {
    const paragraphs: string[] = [];
    let i = startIndex;

    while (i < lines.length) {
      const line = lines[i];

      if (!line.trim()) {
        i++;
        continue;
      }

      if (isHeading(line) || isRule(line) || bulletDepth(line) === -1) {
        break;
      }

      if (bulletDepth(line) !== 0) {
        i++;
        continue;
      }

      const top = bulletText(line);
      i++;

      const children: any[] = [];
      while (i < lines.length) {
        const next = lines[i];
        if (!next.trim()) {
          i++;
          continue;
        }
        if (isHeading(next) || isRule(next)) break;
        const depth = bulletDepth(next);
        if (depth === 0 || depth === -1) break;
        children.push(bulletText(next));
        i++;
      }

      if (!children.length) {
        paragraphs.push(ensureParagraphPunctuation(top));
        continue;
      }

      if (isColonLabel(top)) {
        const label = stripTrailingColon(top);

        const childParts: string[] = [];
        let j = 0;
        while (j < children.length) {
          const child = children[j];

          if (isColonLabel(child)) {
            const subLabel = stripTrailingColon(child);
            j++;

            const subItems: string[] = [];
            while (j < children.length && !isColonLabel(children[j])) {
              subItems.push(children[j]);
              j++;
            }

            if (subItems.length) {
              childParts.push(
                `${subLabel}: ${subItems.join(" ")}`
              );
            } else {
              childParts.push(`${subLabel}.`);
            }
          } else {
            childParts.push(child);
            j++;
          }
        }

        const para = `${label}: ${joinAsSentenceParts(childParts)}`;
        paragraphs.push(ensureParagraphPunctuation(para));
      } else {
        const childParts: string[] = [];
        let j = 0;

        while (j < children.length) {
          const child = children[j];

          if (isColonLabel(child)) {
            const subLabel = stripTrailingColon(child);
            j++;

            const subItems: string[] = [];
            while (j < children.length && !isColonLabel(children[j])) {
              subItems.push(children[j]);
              j++;
            }

            if (subItems.length) {
              childParts.push(`${subLabel}: ${subItems.join(" ")}`);
            } else {
              childParts.push(`${subLabel}.`);
            }
          } else {
            childParts.push(child);
            j++;
          }
        }

        const para = `${top} ${joinAsSentenceParts(childParts)}`;
        paragraphs.push(ensureParagraphPunctuation(para));
      }
    }

    return { paragraphs, nextIndex: i };
  }

  const parseDocument = (text) => {
    const lines = text.split("\n");
    const out: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trimEnd();

      if (!line.trim()) {
        i++;
        continue;
      }

      if (isRule(line)) {
        i++;
        continue;
      }

      if (isHeading(line)) {
        out.push(line);
        out.push("");
        i++;
        continue;
      }

      const depth = bulletDepth(line);
      if (depth === 0) {
        const { paragraphs, nextIndex } = parseBulletBlock(lines, i);
        for (const p of paragraphs) {
          out.push(p);
          out.push("");
        }
        i = nextIndex;
        continue;
      }

      if (depth === -1) {
        const paraLines = [line.trim()];
        i++;

        while (i < lines.length) {
          const next = lines[i];
          if (!next.trim()) {
            i++;
            break;
          }
          if (isHeading(next) || isRule(next) || bulletDepth(next) !== -1) {
            break;
          }
          paraLines.push(next.trim());
          i++;
        }

        out.push(ensureParagraphPunctuation(paraLines.join(" ")));
        out.push("");
        continue;
      }

      i++;
    }

    return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  }

  const decoded = decodeWrappedSummary(rawInput);
  return parseDocument(decoded);
}

  convertToHtmlTable(tableText:any) {
    const rows = tableText.trim().split('\n');
    let html = '\n <div class="table-container-div"><table class="table" border="1">';

    rows.forEach((row: string, index: number) => {
        // Skip separator row (e.g., |----|)
        if (!(/[^|-\s]/g.test(row))) return 
        console.log(row)
        const cells = row.split('|').slice(1, -1).map((cell: string) => cell.trim());
        console.log(cells)
        const tag = index === 0 ? 'th' : 'td'; // Use <th> for the header row, <td> for others
        html += '  <tr>';
        cells.forEach((cell: any) => {
          const isNum = /^\d*(\.\d+)?$/.test(cell)
          const classVal = isNum ? "class=" + (tag == 'th' ? "'grey-bg text-right'" : "'text-right'") :  "class=" + (tag == 'th' ? "'grey-bg text-center'" : "'text-center'");
            html += `    <${tag} `+classVal+`>${cell}</${tag}>`;
        });
        html += '  </tr>';
    });

    html += '</table></div> \n';
    console.log(html)
    return html;
}

    transform(content: any): any {

        content = content?.replace(/\n\n(\|[\s\S]*?\|)\n\n/g,(match:any)=>{
            return match + '   \n\n'
          })
          let res:any = this.restructureToGPT4oStyle(content);
        //   let res:any = content;
           if (content && 1 != 1) {
             const regex = /\n\n(\|[\s\S]*?\|)\n\n/g;
          
             const matches = [...content?.matchAll(regex)].map(match => {
            //    console.log(match)
               return match[0]});
       
               matches.map((match)=>{
                 res = res.replace(match, this.convertToHtmlTable(match))
               })
                 
       
             // }
           }

        res = res
        .replace(/### (.+?)\n/g,"<h5>$1</h5>")
        .replace(/## (.+?)\n/g,"<h5>$1</h5>")        
        //   .replace(/\[(.*?)\]\(\n?(.*?)\)/g, "<a class='ref_links' target='_blank' href='$2'>$1</a>")
        //   .replace(/\[(.*?)\]\(\n?(.*?)\)/g, "<a class='ref_links' target='_blank' href='$2'>$1</a>")
        .replace(/(\!?)\[([^\]]+)\](?!\s)\(([^ ]*)\)/g, (match:any,capture:any,capture1:any,capture2:any) => {
            // if(!Utils.isValidImageURL(capture2)){
            return "<a class='ref_links' target='_blank' href='"+capture2+"'>"+capture1+"</a>"
            // }else {
            //   return match
            // }
        })
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
        .replace(/\[\d+(\.\d+)?(, \d+(\.\d+)?)*\]/g, (match:any,capture:any) => {
            const arr = JSON.parse(match)
            const link = arr.map((e)=>{
        return "<span class='doc_reference stickypopover'>"+ e +"</span>" 
            })
            return  link.join(",");
        })    
        .replace(/(\s)?-\s/gm, "\n <span class='bullettins'></span> ")
        // .replace(/\n/g, '<br/>')
        return  res + `
        `;


        // this.sanitizer.bypassSecurityTrustHtml(res)    
    }

        
  
  }
@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(routes,{useHash:false}),
        FormsModule,
        ReactiveFormsModule,
        NgbModule,
        WebServicesModule.forRoot(startConfig),
        IntlModule.forRoot(AppLocalesConfig),
        LoginModule,
        ModalModule.forRoot(),

        BsSearchModule.forRoot(searchOptions),
        BsAutocompleteModule,
        BsNotificationModule,
        BsFacetModule,
        BsActionModule,
        BsModalModule,
        BsBasketsModule,
        BsAlertsModule,
        BsSavedQueriesModule,
        UtilsModule,
        BsLabelsModule,
        BsUserSettingsModule,
        ResultModule,
        BsFeedbackModule,
        BsPreviewModule,
        MetadataModule,
        BsSelectionModule,
        BsAdvancedModule,
        BsTimelineModule,
        MLModule
    ],
    declarations: [
        AppComponent,
        HomeComponent,
        SearchComponent,
        PreviewComponent,
        SearchFormComponent,
        AutocompleteExtended,
        StickyPopoverDirective,
        HeaderComponent,
        FooterComponent,
        HtmlNodeComponent,
        FiltersComponent,
        FilterModalComponent,
        SecurePipe,
        ResultThumbnailComponent,
        ResultTitleComponent,
        boldStarPipe
    ],
    providers: [
        boldStarPipe,
        // Provides an APP_INITIALIZER which will fetch application configuration information from the Sinequa
        // server automatically at startup using the application name specified in the URL (app[-debug]/<app-name>).
        // This allows an application to avoid hard-coding parameters in the StartConfig but requires that the application
        // be served from the an app[-debug]/<app name> URL.
        { provide: APP_INITIALIZER, useFactory: StartConfigInitializer, deps: [StartConfigWebService], multi: true },

        // Uncomment if the app is to be used with Teams
        { provide: APP_INITIALIZER, useFactory: TeamsInitializer, deps: [AuthenticationService], multi: true },
        PathLocationStrategy,
        // Provides the Angular LocationStrategy to be used for reading route state from the browser's URL. Currently
        // only the HashLocationStrategy is supported by Sinequa.
        { provide: LocationStrategy, useClass: HashLocationStrategy },

        // Provides an HttpInterceptor to handle user login. The LoginInterceptor handles HTTP 401 responses
        // to Sinequa web service requests and initiates the login process.
        { provide: HTTP_INTERCEPTORS, useClass: LoginInterceptor, multi: true },

        // Provides an HttpInterceptor that offers a centralized location through which all client-side
        // audit records pass. An application can replace AuditInterceptor with a subclass that overrides
        // the updateAuditRecord method to add custom audit information to the records.
        { provide: HTTP_INTERCEPTORS, useClass: AuditInterceptor, multi: true },

        // Provides an HttpInterceptor that automatically processes any notifications specified in the $notifications
        // member of the response body to any Sinequa web service requests.
        { provide: HTTP_INTERCEPTORS, useClass: NotificationsInterceptor, multi: true },

        { provide: SCREEN_SIZE_RULES, useValue: breakpoints },

        // Provides default help's folder options
        // this options can be overriden by the custom json configuration from the administration panel
        { provide: APP_HELP_FOLDER_OPTIONS, useValue: HELP_DEFAULT_FOLDER_OPTIONS },

        //FOR LOCAL AUTHORIZATION SAML IS NEEDED
        { provide: HTTP_INTERCEPTORS, useClass: AuthorizationInterceptor, multi: true },
        SecurePipe
    ],
    schemas: [ CUSTOM_ELEMENTS_SCHEMA ],
    bootstrap: [
        AppComponent
    ]
})
export class AppModule {
}
